import Phaser from 'phaser';
import { store } from '../state/store';
import { ITEMS, getItem, type ItemDef } from '../data/items';
import { getCrop } from '../data/crops';
import { getDecoration } from '../data/decorations';
import { TOOLS, type ToolId } from '../data/tools';
import { shopCatalog } from '../data/shop';
import { RECIPES, COOK_RECIPES, type Recipe } from '../data/recipes';
import { GOALS, type Goal } from '../data/goals';
import { ACHIEVEMENTS, achievementsDone } from '../data/achievements';
import { addToShipping, purchase } from '../sim/economy';
import { formatClock } from '../sim/time';
import { seasonOf, yearOf } from '../sim/seasons';
import { personalization } from '../data/personalization';
import { showProfileForm } from '../ui/onboarding';
import { playSfx, setSfxEnabled, setMusicEnabled, isSfxEnabled, isMusicEnabled } from '../audio/sfx';
import { clearSave, saveGame } from '../state/save';
import { getFarmCode, setFarmCode, pullIfNewer } from '../state/cloud';
import { AUDIO, TX, cropTextureKey } from '../registry/keys';
import { getSafeInsets, type Insets } from '../ui/safeArea';
import { hotCropItem, type DayRolloverSummary } from '../state/dayCycle';
import type { WorldScene } from './WorldScene';
import type { InteriorScene } from './InteriorScene';
import type { ForestScene } from './ForestScene';
import type { BeachScene } from './BeachScene';
import type { MineScene } from './MineScene';
import { HATS, getHat, setHat, CLARA_OUTFITS, getClaraOutfit, setClaraOutfit, COLLARS, getCollar, setCollar } from '../data/hats';
import { HOUSE_TIERS } from '../data/house';
import { barnCapacity, BARN_UPGRADES } from '../data/barn';
import { PLAYER_IDEAS } from '../data/credits';
import { confetti } from '../world/celebrate';

const FONT = 'monospace';
/** Decorations that suit indoors — they file under Pip's "Interior" department. */
const INTERIOR_DECOS = new Set(['rug', 'flowerpot', 'lantern', 'pet_bed', 'bench', 'painting', 'table_lamp', 'houseplant', 'chair', 'side_table', 'wall_clock', 'area_rug', 'bed_blue', 'bed_pink', 'dresser']);
/** Which Pip's department an item belongs to. */
function itemDept(def: { id: string; category: string } | undefined): string {
  if (!def) return 'goods';
  if (def.category === 'seed') return 'crops';
  if (def.category === 'animal') return 'animals';
  if (def.category === 'decoration') return INTERIOR_DECOS.has(def.id) ? 'interior' : 'exterior';
  return 'goods';
}
declare const __BUILD_ID__: string;
const APP_VERSION = __BUILD_ID__; // injected from git at build time — auto-updates every deploy, never drifts

/** Overlay scene: HUD (day/clock/money/energy), tool palette, inventory hotbar,
 *  and modals (shop, shipping bin, sleep confirm, day summary). */
export class UIScene extends Phaser.Scene {
  private insets: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

  private dayText!: Phaser.GameObjects.Text;
  private moneyText!: Phaser.GameObjects.Text;
  private energyText!: Phaser.GameObjects.Text;
  private energyBar!: Phaser.GameObjects.Graphics;
  private toastText!: Phaser.GameObjects.Text;

  private toolButtons: { id: ToolId; bg: Phaser.GameObjects.Image }[] = [];
  private hotbar!: Phaser.GameObjects.Container;
  private hotbarSig = '';

  private selectedItemId: string | null = null;
  private selectedTool: ToolId = 'auto';

  private modal: Phaser.GameObjects.Container | null = null;
  private scroll?: { content: Phaser.GameObjects.Container; top: number; bottom: number; min: number; downY: number; startY: number; active: boolean };
  private scrollMoved = false;
  private buildingScroll = false;
  private bigText = false;
  private shopDept = 'crops';
  private rainEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;
  private snowEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;
  private leafEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;
  private bunting: Phaser.GameObjects.GameObject[] = [];

  private topBarH = 42;
  private bottomBandH = 130;

  constructor() {
    super('UI');
  }

  private get world(): WorldScene {
    return this.scene.get('World') as WorldScene;
  }

  create(): void {
    this.insets = getSafeInsets();
    this.makeTopBar();
    this.buildToolbar();
    this.hotbar = this.add.container(0, 0);
    this.toastText = this.add
      .text(this.scale.width / 2, this.insets.top + this.topBarH + 16, '', {
        fontFamily: FONT,
        fontSize: '15px',
        color: '#ffffff',
        backgroundColor: '#00000099',
        padding: { x: 9, y: 4 },
        align: 'center',
      })
      .setOrigin(0.5)
      .setResolution(2)
      .setAlpha(0);

    this.rainEmitter = this.add
      .particles(0, 0, TX.raindrop, {
        x: { min: 0, max: this.scale.width },
        y: -10,
        lifespan: 850,
        speedY: { min: 520, max: 720 },
        speedX: { min: -60, max: -20 },
        scale: { min: 0.8, max: 1.5 },
        alpha: { min: 0.25, max: 0.55 },
        quantity: 3,
        frequency: 26,
        emitting: false,
      })
      .setDepth(900)
      .setScrollFactor(0);

    this.snowEmitter = this.add
      .particles(0, 0, TX.snowflake, {
        x: { min: 0, max: this.scale.width },
        y: -10,
        lifespan: 4200,
        speedY: { min: 28, max: 64 },
        speedX: { min: -22, max: 22 },
        scale: { min: 0.6, max: 1.5 },
        alpha: { min: 0.5, max: 0.95 },
        quantity: 1,
        frequency: 110,
        emitting: false,
      })
      .setDepth(901)
      .setScrollFactor(0);

    this.leafEmitter = this.add
      .particles(0, 0, TX.leaf, {
        x: { min: 0, max: this.scale.width },
        y: -10,
        lifespan: 5000,
        speedY: { min: 24, max: 56 },
        speedX: { min: -30, max: 30 },
        rotate: { min: 0, max: 360 },
        scale: { min: 0.8, max: 1.6 },
        alpha: { min: 0.6, max: 0.95 },
        quantity: 1,
        frequency: 160,
        emitting: false,
      })
      .setDepth(901)
      .setScrollFactor(0);

    this.add
      .text(this.scale.width - 16, this.insets.top + 22, '⚙', { fontFamily: FONT, fontSize: '22px', color: '#ffffff' })
      .setOrigin(0.5)
      .setResolution(2)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => this.openSettings());

    this.add
      .text(this.scale.width - 44, this.insets.top + 22, '★', { fontFamily: FONT, fontSize: '20px', color: '#f2c14e' })
      .setOrigin(0.5)
      .setResolution(2)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => this.openGoals());

    // periodic autosave so progress survives leaving/closing the app (not just on sleep/exit)
    this.time.addEvent({ delay: 10000, loop: true, callback: () => saveGame() });

    // Drag-to-scroll for tall modals (swipe up/down on the list).
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.scrollMoved = false;
      const s = this.scroll;
      if (s) {
        s.active = p.y >= s.top && p.y <= s.bottom;
        s.downY = p.y;
        s.startY = s.content.y;
      }
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      const s = this.scroll;
      if (!s || !s.active || !p.isDown) return;
      const dy = p.y - s.downY;
      if (Math.abs(dy) > 6) this.scrollMoved = true;
      s.content.y = Phaser.Math.Clamp(s.startY + dy, s.min, 0);
    });

    const unsub = store.subscribe(() => this.refresh());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, unsub);
    this.scale.on('resize', () => this.scene.restart());
    this.refresh();

    try {
      this.bigText = localStorage.getItem('sandyhollow.bigtext') === '1';
    } catch {
      /* ignore */
    }
    // First-ever play → show the little manual once.
    try {
      if (!localStorage.getItem('sandyhollow.seenmanual')) {
        localStorage.setItem('sandyhollow.seenmanual', '1');
        this.time.delayedCall(700, () => this.openManual());
      }
    } catch {
      /* ignore */
    }
  }

  // ---- HUD --------------------------------------------------------------------

  private makeTopBar(): void {
    const top = this.insets.top;
    const w = this.scale.width;
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.38);
    bg.fillRoundedRect(6, top + 4, w - 12, this.topBarH, 8);

    this.dayText = this.add
      .text(16, top + 8, '', { fontFamily: FONT, fontSize: '15px', color: '#ffffff' })
      .setResolution(2)
      .setInteractive({ useHandCursor: true });
    this.dayText.on('pointerup', () => this.openStats());

    this.add.image(16, top + 32, TX.energyPip).setScale(0.95).setOrigin(0.5);
    this.energyBar = this.add.graphics();
    this.energyText = this.add
      .text(130, top + 26, '', { fontFamily: FONT, fontSize: '13px', color: '#cfe8c0' })
      .setResolution(2);

    this.add.image(w - 98, top + 18, TX.coin).setScale(1.3);
    this.moneyText = this.add
      .text(w - 84, top + 10, '', { fontFamily: FONT, fontSize: '16px', color: '#ffe9a8' })
      .setResolution(2);
  }

  private refresh(): void {
    const s = store.get();
    this.dayText.setText(`${seasonOf(s.clock.day).name} · Day ${s.clock.day}    ${formatClock(s.clock)}`);
    this.moneyText.setText(`${s.money}`);
    this.energyText
      .setText(`${Math.round(s.energy)}/${s.energyMax}`)
      .setColor(s.energy / s.energyMax < 0.15 ? '#ff8a7a' : '#cfe8c0');

    const top = this.insets.top;
    const frac = Phaser.Math.Clamp(s.energy / s.energyMax, 0, 1);
    this.energyBar.clear();
    this.energyBar.fillStyle(0x000000, 0.4);
    this.energyBar.fillRoundedRect(26, top + 26, 92, 9, 3);
    this.energyBar.fillStyle(frac > 0.3 ? 0x6fcf57 : frac > 0.15 ? 0xe0a13a : 0xe0503a, 1);
    this.energyBar.fillRoundedRect(27, top + 27, Math.max(1, 90 * frac), 7, 3);

    this.rebuildHotbarIfNeeded();
  }

  // ---- tool palette -----------------------------------------------------------

  private buildToolbar(): void {
    const size = 50;
    const gap = 5;
    const totalW = TOOLS.length * size + (TOOLS.length - 1) * gap;
    const startX = Math.max(4, (this.scale.width - totalW) / 2);
    const y = this.bottomTop() + 6;
    this.toolButtons = TOOLS.map((tool, i) => {
      const x = startX + i * (size + gap);
      const bg = this.add
        .image(x, y, TX.uiSlot)
        .setOrigin(0, 0)
        .setDisplaySize(size, size)
        .setInteractive({ useHandCursor: true });
      this.add.image(x + size / 2, y + size / 2 - 5, tool.icon).setScale(2.2);
      this.add
        .text(x + size / 2, y + size - 11, tool.name, { fontFamily: FONT, fontSize: '9px', color: '#2a3326' })
        .setOrigin(0.5)
        .setResolution(2);
      bg.on('pointerup', () => this.onToolTap(tool.id));
      return { id: tool.id, bg };
    });
    this.highlightTools();
  }

  private highlightTools(): void {
    for (const b of this.toolButtons) {
      b.bg.setTexture(b.id === this.selectedTool ? TX.uiSlotActive : TX.uiSlot);
    }
  }

  private onToolTap(id: ToolId): void {
    this.selectedTool = id;
    this.selectedItemId = null;
    this.world.exitPlacement();
    this.highlightTools();
    this.hotbarSig = '';
    this.refresh();
    const tool = TOOLS.find((t) => t.id === id);
    if (tool) this.showToast(`${tool.name} — ${tool.hint}`);
  }

  // ---- inventory hotbar -------------------------------------------------------

  private bottomTop(): number {
    return this.scale.height - this.insets.bottom - this.bottomBandH;
  }

  private rebuildHotbarIfNeeded(): void {
    const s = store.get();
    const items = s.inventory.filter((slot) => {
      const def = getItem(slot.itemId);
      return def && (def.category === 'seed' || def.category === 'decoration');
    });
    const sig = items.map((i) => `${i.itemId}:${i.qty}`).join('|') + `#${this.selectedItemId}`;
    if (sig === this.hotbarSig) return;
    this.hotbarSig = sig;

    this.hotbar.removeAll(true);
    const slot = 42;
    const gap = 6;
    const totalW = items.length * slot + (items.length - 1) * gap;
    const startX = Math.max(8, (this.scale.width - totalW) / 2);
    const y = this.bottomTop() + 70;

    items.forEach((it, i) => {
      const x = startX + i * (slot + gap);
      const active = it.itemId === this.selectedItemId;
      const cell = this.add
        .image(x, y, active ? TX.uiSlotActive : TX.uiSlot)
        .setOrigin(0, 0)
        .setDisplaySize(slot, slot)
        .setInteractive({ useHandCursor: true });
      const icon = this.add.image(x + slot / 2, y + slot / 2 - 2, getItem(it.itemId)!.icon).setScale(1.7);
      const count = this.add
        .text(x + slot - 4, y + slot - 4, `${it.qty}`, { fontFamily: FONT, fontSize: '11px', color: '#ffffff' })
        .setOrigin(1, 1)
        .setResolution(2);
      const label = this.add
        .text(x + slot / 2, y + slot, getItem(it.itemId)!.name, {
          fontFamily: FONT,
          fontSize: '7px',
          color: '#cfe0c4',
          align: 'center',
          wordWrap: { width: slot + 8 },
          lineSpacing: -3,
        })
        .setOrigin(0.5, 0)
        .setResolution(3);
      cell.on('pointerup', () => this.onSlotTap(it.itemId));
      this.hotbar.add([cell, icon, count, label]);
    });
  }

  private onSlotTap(itemId: string): void {
    const def = getItem(itemId);
    if (!def) return;
    if (itemId === 'fertilizer') {
      this.useFertilizer();
      return;
    }
    if (def.energy && def.energy > 0) {
      this.eatItem(def);
      return;
    }
    this.selectedTool = 'auto';
    this.highlightTools();
    if (this.selectedItemId === itemId) {
      this.selectedItemId = null;
      if (def.category === 'decoration') this.world.exitPlacement();
    } else {
      this.selectedItemId = itemId;
      if (def.category === 'decoration') this.world.enterPlacement(itemId);
      else this.world.exitPlacement();
      this.showToast(`${def.name} — ${def.description}`);
    }
    this.hotbarSig = '';
    this.refresh();
  }

  // ---- queried by WorldScene --------------------------------------------------

  get selectedSeedId(): string | null {
    const def = this.selectedItemId ? getItem(this.selectedItemId) : undefined;
    return def && def.category === 'seed' ? def.id : null;
  }
  get selectedDecoId(): string | null {
    const def = this.selectedItemId ? getItem(this.selectedItemId) : undefined;
    return def && def.category === 'decoration' ? def.id : null;
  }
  /** A produce/dish item selected in the bag — giftable to Clara. */
  get selectedGiftId(): string | null {
    const def = this.selectedItemId ? getItem(this.selectedItemId) : undefined;
    return def && def.category === 'crop' ? def.id : null;
  }
  get selectedToolId(): ToolId {
    return this.selectedTool;
  }

  clearSelection(): void {
    this.selectedItemId = null;
    this.hotbarSig = '';
    this.refresh();
  }

  consumesPointer(_x: number, y: number): boolean {
    if (this.modal) return true;
    return y < this.insets.top + this.topBarH + 8 || y > this.bottomTop();
  }

  isModalOpen(): boolean {
    return this.modal !== null;
  }

  showRain(on: boolean): void {
    if (!this.rainEmitter) return;
    if (on) this.rainEmitter.start();
    else this.rainEmitter.stop();
  }

  showSnow(on: boolean): void {
    if (!this.snowEmitter) return;
    if (on) this.snowEmitter.start();
    else this.snowEmitter.stop();
  }

  showLeaves(on: boolean): void {
    if (!this.leafEmitter) return;
    if (on) this.leafEmitter.start();
    else this.leafEmitter.stop();
  }

  /** Full-screen view of the framed family photo. */
  showPhoto(): void {
    const c = this.openModal();
    const pw = 300;
    const ph = 400;
    const { x, y } = this.panel(c, pw, ph);
    const img = this.add.image(x + pw / 2, y + 22, TX.photo).setOrigin(0.5, 0);
    img.setScale(Math.min((pw - 36) / img.width, (ph - 92) / img.height));
    c.add(img);
    this.label(c, x + pw / 2, y + ph - 54, personalization.greeting, 12, '#ffe9a8', 0.5);
    this.button(c, x + pw / 2 - 50, y + ph - 34, 100, 'Done', () => this.closeModal(), 0xb0a48f);
  }

  /** Festival bunting strung across the top on fair days. */
  setFestive(on: boolean): void {
    if (on && this.bunting.length === 0) {
      const y = this.insets.top + 46;
      const colors = [0xe0443a, 0xf2c14e, 0x5ab0d8, 0x6ab04a, 0xe06d8b];
      const n = Math.ceil(this.scale.width / 18) + 1;
      for (let i = 0; i < n; i++) {
        this.bunting.push(
          this.add.image(i * 18, y, TX.bunting).setOrigin(0, 0).setTint(colors[i % colors.length]!).setDepth(40),
        );
      }
      // bobbing balloons
      const bcol = [0xff5a5a, 0x5a9ad0, 0xffd24a, 0x5ad06a];
      for (let i = 0; i < 4; i++) {
        const bx = 36 + (i * (this.scale.width - 72)) / 3;
        const balloon = this.add.ellipse(bx, this.insets.top + 78, 15, 19, bcol[i]!).setDepth(41).setScrollFactor(0);
        this.bunting.push(balloon);
        this.tweens.add({ targets: balloon, y: (balloon.y as number) + 8, duration: 1500 + i * 220, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      }
    } else if (!on && this.bunting.length) {
      this.bunting.forEach((b) => b.destroy());
      this.bunting = [];
    }
  }

  // ---- toast ------------------------------------------------------------------

  showToast(msg: string, kind: 'info' | 'warn' | 'good' = 'info'): void {
    const color = kind === 'warn' ? '#ffcaa8' : kind === 'good' ? '#c8f5b0' : '#ffffff';
    this.toastText.setText(msg).setColor(color).setAlpha(1);
    this.tweens.killTweensOf(this.toastText);
    this.tweens.add({ targets: this.toastText, alpha: 0, delay: 1500, duration: 500 });
  }

  // ---- modal helpers ----------------------------------------------------------

  private openModal(): Phaser.GameObjects.Container {
    this.closeModal();
    const c = this.add.container(0, 0).setDepth(1000);
    c.add(
      this.add
        .rectangle(0, 0, this.scale.width, this.scale.height, 0x000000, 0.5)
        .setOrigin(0, 0)
        .setInteractive(),
    );
    this.modal = c;
    return c;
  }
  private closeModal(): void {
    this.modal?.destroy();
    this.modal = null;
    this.scroll = undefined;
  }

  /** A vertically scrollable content sub-container clipped to [top, top+viewH]. */
  private scrollList(
    c: Phaser.GameObjects.Container,
    panelX: number,
    panelW: number,
    top: number,
    viewH: number,
    contentH: number,
    build: (content: Phaser.GameObjects.Container) => void,
  ): void {
    const content = this.add.container(0, 0);
    c.add(content);
    this.buildingScroll = true;
    build(content);
    this.buildingScroll = false;
    const maskG = this.add.graphics().setVisible(false);
    maskG.fillStyle(0xffffff, 1);
    maskG.fillRect(panelX, top, panelW, viewH);
    c.add(maskG);
    content.setMask(maskG.createGeometryMask());
    this.scroll = { content, top, bottom: top + viewH, min: Math.min(0, viewH - contentH), downY: 0, startY: 0, active: false };
  }
  private panel(c: Phaser.GameObjects.Container, w: number, h: number): { x: number; y: number } {
    const x = (this.scale.width - w) / 2;
    const y = (this.scale.height - h) / 2;
    const g = this.add.graphics();
    g.fillStyle(0x2d3b2f, 0.98);
    g.fillRoundedRect(x, y, w, h, 10);
    g.lineStyle(2, 0x7dbf5a, 1);
    g.strokeRoundedRect(x, y, w, h, 10);
    c.add(g);
    return { x, y };
  }
  private button(
    c: Phaser.GameObjects.Container,
    x: number,
    y: number,
    w: number,
    label: string,
    onTap: () => void,
    tint = 0x7dbf5a,
  ): void {
    const g = this.add.graphics();
    g.fillStyle(tint, 1);
    g.fillRoundedRect(x, y, w, 30, 6);
    c.add(g);
    c.add(
      this.add
        .text(x + w / 2, y + 15, label, { fontFamily: FONT, fontSize: this.bigText ? '15px' : '14px', color: '#1c241d', align: 'center', wordWrap: { width: w - 10 } })
        .setOrigin(0.5)
        .setResolution(2),
    );
    const bounded = this.buildingScroll;
    const hit = this.add.rectangle(x, y, w, 30, 0xffffff, 0).setOrigin(0, 0).setInteractive({ useHandCursor: true });
    hit.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (this.scrollMoved) return; // a drag, not a tap
      if (bounded && this.scroll && (p.y < this.scroll.top || p.y > this.scroll.bottom)) return; // scrolled out of view
      playSfx(this, AUDIO.click);
      onTap();
    });
    c.add(hit);
  }
  private label(c: Phaser.GameObjects.Container, x: number, y: number, t: string, size: number, color: string, origin = 0): void {
    const sz = Math.max(9, this.bigText ? size + 2 : size); // respect the designed size so it fits the box; gentle +2 in big-text
    c.add(
      this.add
        .text(x, y, t, { fontFamily: FONT, fontSize: `${sz}px`, color })
        .setOrigin(origin, 0)
        .setResolution(2),
    );
  }

  // ---- sleep / day summary ----------------------------------------------------

  confirmSleep(onConfirm: () => void): void {
    const c = this.openModal();
    const w = 240;
    const h = 130;
    const { x, y } = this.panel(c, w, h);
    this.label(c, x + w / 2, y + 24, 'Head inside to sleep?', 15, '#ffffff', 0.5);
    this.label(c, x + w / 2, y + 50, 'Crops grow & the bin pays out.', 10, '#bcd0b4', 0.5);
    this.button(c, x + 16, y + 80, 92, 'Sleep', () => {
      this.closeModal();
      onConfirm();
    });
    this.button(c, x + w - 108, y + 80, 92, 'Not yet', () => this.closeModal(), 0xb0a48f);
  }

  showDaySummary(summary: DayRolloverSummary): void {
    const c = this.openModal();
    const w = 240;
    const h = 150;
    const { x, y } = this.panel(c, w, h);
    this.label(c, x + w / 2, y + 24, `Day ${summary.newDay}`, 20, '#ffffff', 0.5);
    this.label(c, x + w / 2, y + 58, `Shipping earned  +${summary.earned}g`, 13, '#ffe9a8', 0.5);
    this.label(c, x + w / 2, y + 80, `${summary.cropsAdvanced} crop(s) grew`, 12, '#c8f5b0', 0.5);
    this.button(c, x + w / 2 - 50, y + h - 40, 100, 'Good morning', () => this.closeModal());
  }

  // ---- shipping bin -----------------------------------------------------------

  openShipping(): void {
    const render = (): void => {
      const c = this.openModal();
      const w = 280;
      const h = 262;
      const { x, y } = this.panel(c, w, h);
      this.label(c, x + w / 2, y + 16, 'Shipping Bin', 16, '#ffffff', 0.5);
      const hotId = hotCropItem(store.get().clock.day);
      const hotDef = getItem(hotId);
      if (hotDef) this.label(c, x + w / 2, y + 34, `⭐ ${hotDef.name} +50% today!`, 11, '#9ff09f', 0.5);

      const sellable = store.get().inventory.filter((slot) => {
        const def = ITEMS[slot.itemId];
        return def && def.sellPrice > 0 && slot.qty > 0;
      });

      if (sellable.length === 0) {
        this.label(c, x + w / 2, y + h / 2, 'Nothing to ship yet —\nharvest some crops!', 12, '#bcd0b4', 0.5);
      } else {
        sellable.slice(0, 5).forEach((slot, i) => {
          const def = ITEMS[slot.itemId]!;
          const ry = y + 54 + i * 34;
          const isHot = slot.itemId === hotId;
          c.add(this.add.image(x + 20, ry + 8, def.icon).setScale(1.5));
          this.label(c, x + 36, ry + 1, `${def.name}  x${slot.qty}`, 12, '#ffffff');
          this.label(c, x + 36, ry + 16, isHot ? `${Math.round(def.sellPrice * 1.5)}g each ⭐` : `${def.sellPrice}g each`, 10, isHot ? '#9ff09f' : '#ffe9a8');
          this.button(c, x + w - 76, ry, 60, 'Ship 1', () => {
            if (store.removeItem(slot.itemId, 1)) {
              store.update((st) => {
                st.shippingBin = addToShipping(st.shippingBin, slot.itemId, 1);
              });
              playSfx(this, AUDIO.coin);
              render();
            }
          });
        });
      }
      this.button(c, x + w / 2 - 50, y + h - 36, 100, 'Done', () => this.closeModal(), 0xb0a48f);
    };
    render();
  }

  // ---- shop -------------------------------------------------------------------

  /** The farmer's almanac — season, today's weather, and what to plant. */
  openAlmanac(): void {
    const c = this.openModal();
    const w = 290;
    const h = 244;
    const { x, y } = this.panel(c, w, h);
    const s = store.get();
    const season = seasonOf(s.clock.day);
    const tend: Record<string, string> = { spring: 'wet & green', summer: 'hot & dry', fall: 'cool & breezy', winter: 'cold & snowy' };
    const recs: Record<string, string> = {
      spring: 'Strawberry · Potato · Carrot · Cabbage',
      summer: 'Tomato · Pepper · Melon · Corn',
      fall: 'Pumpkin · Squash · Leek · Eggplant',
      winter: 'Rest, mine & renovate ❄️',
    };
    this.label(c, x + w / 2, y + 16, "📖 Farmer's Almanac", 15, '#ffffff', 0.5);
    this.label(c, x + 16, y + 42, `${season.name} · Day ${s.clock.day} · Year ${yearOf(s.clock.day)}`, 12, '#cfe0c4');
    this.label(c, x + 16, y + 64, s.weather === 'rain' ? "Today: Rainy 🌧️  (crops self-water)" : 'Today: Clear ☀️', 11, s.weather === 'rain' ? '#9fd0e6' : '#ffe9a8');
    this.label(c, x + 16, y + 82, `${season.name} tends to be ${tend[season.id] ?? ''}.`, 11, '#bcd0b4');
    this.label(c, x + 16, y + 112, 'Good to plant now:', 12, '#ffffff');
    this.label(c, x + 16, y + 132, recs[season.id] ?? '', 11, '#9ff09f');
    this.label(c, x + 16, y + 162, "Tip: sell at Pip's on a ⭐ deal day,", 10, '#bcd0b4');
    this.label(c, x + 16, y + 176, 'and ship the day’s hot crop for +50%.', 10, '#bcd0b4');
    this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Close', () => this.closeModal(), 0xb0a48f);
  }

  /** The barn — animal capacity + upgrades. */
  openBarnUpgrade(): void {
    const render = (): void => {
      const c = this.openModal();
      const w = 270;
      const h = 212;
      const { x, y } = this.panel(c, w, h);
      const lvl = store.get().barnLevel;
      this.label(c, x + w / 2, y + 16, '🛖 Barn', 16, '#ffffff', 0.5);
      this.label(c, x + w / 2, y + 46, `Animals: ${store.get().animals.length} / ${barnCapacity(lvl)}`, 13, '#ffe9a8', 0.5);
      this.label(c, x + w / 2, y + 66, `Barn level ${lvl + 1} · happier animals give more`, 10, '#cfe0c4', 0.5);
      const up = BARN_UPGRADES[lvl];
      if (!up) {
        this.label(c, x + w / 2, y + 116, 'Your barn is fully upgraded! 🐄', 12, '#9ff09f', 0.5);
      } else {
        const costParts = [`${up.cost.gold}g`, ...(up.cost.items ?? []).map((it) => `${it.qty} ${getItem(it.itemId)?.name ?? it.itemId}`)];
        const can = store.get().money >= up.cost.gold && (up.cost.items ?? []).every((it) => store.countItem(it.itemId) >= it.qty);
        this.label(c, x + w / 2, y + 100, `Upgrade → holds ${barnCapacity(lvl + 1)} animals`, 12, '#ffffff', 0.5);
        this.label(c, x + w / 2, y + 118, costParts.join(' · '), 11, can ? '#9ff09f' : '#e0a0a0', 0.5);
        this.button(
          c,
          x + 20,
          y + h - 66,
          w - 40,
          can ? 'Upgrade Barn' : 'Not enough yet',
          () => {
            if (store.get().money < up.cost.gold || !(up.cost.items ?? []).every((it) => store.countItem(it.itemId) >= it.qty)) return;
            store.update((s) => {
              s.money -= up.cost.gold;
              s.barnLevel = lvl + 1;
            });
            for (const it of up.cost.items ?? []) store.removeItem(it.itemId, it.qty);
            playSfx(this, AUDIO.coin);
            this.showToast(`🛖 Barn upgraded — now holds ${barnCapacity(lvl + 1)} animals!`, 'good');
            render();
          },
          can ? 0x7dbf5a : 0x6a6258,
        );
      }
      this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Done', () => this.closeModal(), 0xb0a48f);
    };
    render();
  }

  /** Renovate the house — upgrade tier by tier up to a castle. */
  openHouseUpgrade(): void {
    const render = (): void => {
      const c = this.openModal();
      const w = 280;
      const h = 230;
      const { x, y } = this.panel(c, w, h);
      const level = store.get().houseLevel;
      const cur = HOUSE_TIERS[level]!;
      const next = HOUSE_TIERS[level + 1];
      this.label(c, x + w / 2, y + 14, '🔨 Renovate', 16, '#ffffff', 0.5);
      const img = this.add.image(x + w / 2, y + 88, cur.texture).setOrigin(0.5, 1);
      img.setScale(Math.min(1.6, 48 / img.height));
      c.add(img);
      this.label(c, x + w / 2, y + 94, `Now: ${cur.name}`, 12, '#cfe0c4', 0.5);
      if (!next) {
        this.label(c, x + w / 2, y + 124, 'Your castle is complete! 👑', 12, '#ffe9a8', 0.5);
        this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Done', () => this.closeModal(), 0xb0a48f);
        return;
      }
      const costParts = [`${next.cost.gold}g`, ...(next.cost.items ?? []).map((it) => `${it.qty} ${getItem(it.itemId)?.name ?? it.itemId}`)];
      const can = store.get().money >= next.cost.gold && (next.cost.items ?? []).every((it) => store.countItem(it.itemId) >= it.qty);
      this.label(c, x + w / 2, y + 112, `Next: ${next.name}`, 13, '#ffffff', 0.5);
      this.label(c, x + w / 2, y + 130, costParts.join(' · '), 11, can ? '#9ff09f' : '#e0a0a0', 0.5);
      this.button(c, x + 20, y + 152, w - 40, can ? `Build the ${next.name}` : 'Not enough yet', () => {
        if (this.world.upgradeHouse()) render();
      }, can ? 0x7dbf5a : 0x6a6258);
      this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Done', () => this.closeModal(), 0xb0a48f);
    };
    render();
  }

  /** Town notice board — a daily fetch request for a tidy gold reward. */
  openQuestBoard(): void {
    const day = store.get().clock.day;
    const wants = ['tomato', 'milk', 'egg', 'strawberry', 'wool', 'honey', 'corn', 'potato', 'fish', 'wood'];
    const wantId = wants[day % wants.length]!;
    const qty = 2;
    const def = getItem(wantId);
    const reward = Math.max(80, (def?.sellPrice ?? 30) * 3 * qty);
    const c = this.openModal();
    const w = 264;
    const h = 212;
    const { x, y } = this.panel(c, w, h);
    this.label(c, x + w / 2, y + 16, '📋 Town Notice Board', 15, '#ffffff', 0.5);
    if (store.get().flags['forequest']) {
      this.label(c, x + w / 2, y + 90, "Today's request is filled —", 11, '#9ff09f', 0.5);
      this.label(c, x + w / 2, y + 108, 'thank you! 💛 Come back tomorrow.', 11, '#9ff09f', 0.5);
    } else {
      this.label(c, x + w / 2, y + 48, `Wanted: ${qty} × ${def?.name ?? wantId}`, 13, '#ffe9a8', 0.5);
      if (def) c.add(this.add.image(x + w / 2, y + 92, def.icon).setScale(2.2));
      this.label(c, x + w / 2, y + 120, `Reward: ${reward}g`, 12, '#cfe0c4', 0.5);
      const have = store.countItem(wantId) >= qty;
      this.button(
        c,
        x + 20,
        y + h - 64,
        w - 40,
        have ? 'Deliver 💛' : `Need ${qty} × ${def?.name ?? wantId}`,
        () => {
          if (store.countItem(wantId) < qty) return;
          store.removeItem(wantId, qty);
          store.update((s) => {
            s.money += reward;
            s.flags['forequest'] = true;
          });
          playSfx(this, AUDIO.coin);
          this.closeModal();
          this.showToast(`Delivered to the town! +${reward}g 💛`, 'good');
        },
        have ? 0x7dbf5a : 0x6a6258,
      );
    }
    this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Close', () => this.closeModal(), 0xb0a48f);
  }

  /** The traveling vendor's cart — a small rotating set of handy wares (market days in town). */
  openVendor(): void {
    const day = store.get().clock.day;
    const pool: { itemId: string; price: number }[] = [
      { itemId: 'fertilizer', price: 28 },
      { itemId: 'honey', price: 120 },
      { itemId: 'cake', price: 150 },
      { itemId: 'seed_strawberry', price: 35 },
      { itemId: 'seed_melon', price: 40 },
      { itemId: 'wood', price: 4 },
      { itemId: 'stone', price: 7 },
      { itemId: 'coal', price: 18 },
    ];
    const offers = [0, 1, 2, 3].map((i) => pool[(day * 3 + i) % pool.length]!);
    const render = (): void => {
      const c = this.openModal();
      const cols = 2;
      const cw = 110;
      const ch = 96;
      const w = cols * cw + 24;
      const h = 56 + 2 * ch + 38;
      const { x, y } = this.panel(c, w, h);
      this.label(c, x + w / 2, y + 14, '🛒 Traveling Vendor', 15, '#ffffff', 0.5);
      this.label(c, x + w / 2, y + 34, 'Rare wares, just passing through!', 10, '#cfe0c4', 0.5);
      offers.forEach((entry, i) => {
        const def = getItem(entry.itemId);
        if (!def) return;
        const { previewKey, title } = this.shopPreview(entry.itemId, def);
        const cx = x + 12 + (i % cols) * cw;
        const cy = y + 50 + Math.floor(i / cols) * ch;
        const mid = cx + (cw - 8) / 2;
        const afford = store.get().money >= entry.price;
        c.add(this.add.image(cx, cy, TX.uiSlot).setOrigin(0, 0).setDisplaySize(cw - 8, ch - 8));
        c.add(this.add.image(mid, cy + 26, previewKey).setScale(1.6).setAlpha(afford ? 1 : 0.45));
        this.label(c, mid, cy + 52, title, 10, '#ffffff', 0.5);
        this.label(c, mid, cy + 68, `${entry.price}g`, 11, afford ? '#ffe9a8' : '#a08c6a', 0.5);
        const hit = this.add.rectangle(cx, cy, cw - 8, ch - 8, 0xffffff, 0).setOrigin(0, 0).setInteractive({ useHandCursor: true });
        hit.on('pointerup', () => this.buy(entry.itemId, entry.price, title, render));
        c.add(hit);
      });
      this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Bye', () => this.closeModal(), 0xb0a48f);
    };
    render();
  }

  /** Whack-a-Mole at the town game booth — 20s, bonk moles for gold (once a day). */
  openMoleGame(): void {
    if (!store.toddlerMode && store.get().flags['foragemole']) {
      this.showToast('Come back tomorrow for another game! 🎯', 'info');
      return;
    }
    store.update((s) => (s.flags['foragemole'] = true));
    const c = this.openModal();
    const w = 280;
    const h = 296;
    const { x, y } = this.panel(c, w, h);
    this.label(c, x + w / 2, y + 14, '🎯 Whack-a-Mole!', 16, '#ffffff', 0.5);
    let score = 0;
    let timeLeft = 20;
    const scoreLbl = this.add.text(x + 16, y + 38, 'Score: 0', { fontFamily: FONT, fontSize: '12px', color: '#ffe9a8' }).setResolution(2);
    const timeLbl = this.add.text(x + w - 16, y + 38, '⏱ 20', { fontFamily: FONT, fontSize: '12px', color: '#cfe0c4' }).setOrigin(1, 0).setResolution(2);
    c.add([scoreLbl, timeLbl]);

    const cols = 3;
    const cell = 78;
    const gx = x + (w - cols * cell) / 2 + cell / 2;
    const gy = y + 104;
    interface Hole {
      hx: number;
      hy: number;
      mole: Phaser.GameObjects.Image | null;
    }
    const holes: Hole[] = [];
    for (let r = 0; r < 2; r++) {
      for (let cI = 0; cI < cols; cI++) {
        const hx = gx + cI * cell;
        const hy = gy + r * 76;
        c.add(this.add.ellipse(hx, hy + 14, 46, 18, 0x2e241c));
        holes.push({ hx, hy, mole: null });
      }
    }

    let pop: Phaser.Time.TimerEvent;
    let tick: Phaser.Time.TimerEvent;
    const end = (): void => {
      pop?.remove();
      tick?.remove();
      const gold = score * 12;
      store.update((s) => (s.money += gold));
      playSfx(this, AUDIO.coin);
      this.closeModal();
      this.showToast(`🎯 ${score} bonks! +${gold}g`, 'good');
    };
    const popMole = (): void => {
      const empty = holes.filter((hl) => !hl.mole);
      if (empty.length === 0) return;
      const hl = empty[Math.floor(Math.random() * empty.length)]!;
      const m = this.add.image(hl.hx, hl.hy + 18, TX.mole).setScale(2).setDepth(1001).setInteractive({ useHandCursor: true });
      c.add(m);
      hl.mole = m;
      this.tweens.add({ targets: m, y: hl.hy + 2, duration: 120, ease: 'Back.easeOut' });
      m.on('pointerdown', () => {
        if (hl.mole !== m || m.getData('bonked')) return;
        m.setData('bonked', true); // keep the hole occupied until the bonk finishes (no double-pop)
        m.disableInteractive();
        score++;
        scoreLbl.setText(`Score: ${score}`);
        playSfx(this, AUDIO.pop);
        this.tweens.add({
          targets: m,
          scaleX: 2.5,
          scaleY: 1.3,
          duration: 80,
          yoyo: true,
          onComplete: () => {
            hl.mole = null;
            m.destroy();
          },
        });
      });
      this.time.delayedCall(Phaser.Math.Between(700, 1200), () => {
        if (hl.mole === m && !m.getData('bonked')) {
          hl.mole = null;
          this.tweens.add({ targets: m, y: hl.hy + 18, alpha: 0, duration: 150, onComplete: () => m.destroy() });
        }
      });
    };
    pop = this.time.addEvent({ delay: 640, loop: true, callback: popMole });
    tick = this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        timeLeft--;
        timeLbl.setText(`⏱ ${timeLeft}`);
        if (timeLeft <= 0) end();
      },
    });
    this.button(c, x + w / 2 - 50, y + h - 30, 100, 'Done', () => end(), 0xb0a48f);
  }

  /** Pip's Stall front desk: Buy (the shop) or Sell (the shipping bin). */
  openStall(): void {
    const c = this.openModal();
    const w = 240;
    const h = 168;
    const { x, y } = this.panel(c, w, h);
    this.label(c, x + w / 2, y + 18, "Pip's Stall", 18, '#ffffff', 0.5);
    this.label(c, x + w / 2, y + 42, 'What can I do for you?', 11, '#bcd0b4', 0.5);
    this.button(c, x + 20, y + 66, w - 40, '🛒 Buy', () => this.openShop(), 0x7dbf5a);
    this.button(c, x + 20, y + 104, w - 40, '💰 Sell', () => this.openShipping(), 0xe0a13a);
    this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Bye', () => this.closeModal(), 0xb0a48f);
  }

  openShop(): void {
    const base = shopCatalog();
    const day = store.get().clock.day;
    const merchantDay = day % 4 === 0; // a traveling merchant visits
    const seasonId = seasonOf(day).id;
    const seasonal =
      seasonId === 'fall'
        ? [{ itemId: 'pumpkin_deco', price: 110 }]
        : seasonId === 'winter'
          ? [{ itemId: 'snowman', price: 110 }]
          : [];
    const merchant = merchantDay
      ? [{ itemId: 'gem', price: 240 }, { itemId: 'honey', price: 140 }, { itemId: 'cake', price: 180 }, { itemId: 'copper_bar', price: 130 }]
      : [];
    const catalog = [...base, ...seasonal, ...merchant];
    const dealItemId = base[day % base.length]?.itemId; // a daily deal among the regular stock
    const DEPTS = [
      { id: 'crops', label: '🌱 Seeds' },
      { id: 'animals', label: '🐔 Animals' },
      { id: 'exterior', label: '🏡 Yard' },
      { id: 'interior', label: '🛋 Home' },
      { id: 'goods', label: '🛒 Goods' },
    ];
    const render = (): void => {
      const c = this.openModal();
      const depts = DEPTS.filter((d) => catalog.some((e) => itemDept(getItem(e.itemId)) === d.id));
      if (!depts.some((d) => d.id === this.shopDept)) this.shopDept = depts[0]?.id ?? 'crops';
      const items = catalog.filter((e) => itemDept(getItem(e.itemId)) === this.shopDept);
      const cols = 4;
      const cw = 74;
      const ch = 82;
      const w = cols * cw + 20;
      const rows = Math.max(1, Math.ceil(items.length / cols));
      const h = 86 + rows * ch + 38;
      const { x, y } = this.panel(c, w, h);
      this.label(c, x + 14, y + 14, merchantDay ? "Pip's · 🛒 Merchant!" : "Pip's Stall", 13, '#ffffff');
      this.label(c, x + w - 14, y + 16, `${store.get().money}g`, 13, '#ffe9a8', 1);

      // department tabs
      const tabW = (w - 20) / depts.length;
      depts.forEach((d, i) => {
        const tx = x + 10 + i * tabW;
        const active = d.id === this.shopDept;
        const bg = this.add.graphics();
        bg.fillStyle(active ? 0x7dbf5a : 0x3a4636, 1);
        bg.fillRoundedRect(tx, y + 32, tabW - 3, 22, 4);
        c.add(bg);
        c.add(this.add.text(tx + (tabW - 3) / 2, y + 43, d.label, { fontFamily: FONT, fontSize: '9px', color: active ? '#1c241d' : '#cfe0c4' }).setOrigin(0.5).setResolution(2));
        const hit = this.add.rectangle(tx, y + 32, tabW - 3, 22, 0xffffff, 0).setOrigin(0, 0).setInteractive({ useHandCursor: true });
        hit.on('pointerup', () => {
          this.shopDept = d.id;
          render();
        });
        c.add(hit);
      });

      items.forEach((entry, i) => {
        const def = getItem(entry.itemId);
        if (!def) return;
        const { previewKey, title } = this.shopPreview(entry.itemId, def);
        const cx = x + 10 + (i % cols) * cw;
        const cy = y + 62 + Math.floor(i / cols) * ch;
        const mid = cx + (cw - 6) / 2;
        const isDeal = entry.itemId === dealItemId;
        const price = isDeal ? Math.floor(entry.price * 0.7) : entry.price;
        const afford = store.get().money >= price;
        c.add(this.add.image(cx, cy, TX.uiSlot).setOrigin(0, 0).setDisplaySize(cw - 6, ch - 6));
        c.add(this.add.image(mid, cy + 22, previewKey).setScale(1.4).setAlpha(afford ? 1 : 0.45));
        this.label(c, mid, cy + 46, title, 9, '#ffffff', 0.5);
        this.label(c, mid, cy + 60, `${price}g${isDeal ? ' ⭐' : ''}`, 10, afford ? (isDeal ? '#9ff09f' : '#ffe9a8') : '#a08c6a', 0.5);
        const hit = this.add
          .rectangle(cx, cy, cw - 6, ch - 6, 0xffffff, 0)
          .setOrigin(0, 0)
          .setInteractive({ useHandCursor: true });
        hit.on('pointerup', () => this.buy(entry.itemId, price, title, render));
        c.add(hit);
      });

      this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Done', () => this.closeModal(), 0xb0a48f);
    };
    render();
  }

  /** Show the *final product*: the grown crop for seeds, the object for decorations. */
  private shopPreview(itemId: string, def: ItemDef): { previewKey: string; title: string } {
    if (def.plantsCrop) {
      const crop = getCrop(def.plantsCrop);
      if (crop) return { previewKey: cropTextureKey(crop.id, crop.stages - 1), title: crop.name };
    }
    const d = getDecoration(itemId);
    if (d) return { previewKey: d.w > 1 || d.h > 1 ? d.icon : d.texture, title: d.name }; // multi-tile → small icon
    return { previewKey: def.icon, title: def.name };
  }

  // ---- house storage chest ----------------------------------------------------

  openStorage(): void {
    const render = (): void => {
      const c = this.openModal();
      const w = 300;
      const h = 286;
      const { x, y } = this.panel(c, w, h);
      this.label(c, x + w / 2, y + 16, 'Storage Chest', 16, '#ffffff', 0.5);
      this.label(c, x + 72, y + 40, 'Bag  →', 11, '#cfe0c4', 0.5);
      this.label(c, x + w - 72, y + 40, '←  Chest', 11, '#cfe0c4', 0.5);

      const col = (items: { itemId: string; qty: number }[], cx: number, toChest: boolean): void => {
        if (items.length === 0) this.label(c, cx + 40, y + 90, '(empty)', 10, '#8aa085', 0.5);
        items.slice(0, 6).forEach((slot, i) => {
          const def = getItem(slot.itemId);
          if (!def) return;
          const ry = y + 56 + i * 32;
          c.add(this.add.image(cx, ry + 9, def.icon).setScale(1.3));
          this.label(c, cx + 14, ry + 3, `${def.name}  x${slot.qty}`, 10, '#ffffff');
          const hit = this.add.rectangle(cx - 12, ry, 118, 28, 0xffffff, 0).setOrigin(0, 0).setInteractive({ useHandCursor: true });
          hit.on('pointerup', () => {
            if (toChest) store.moveToStorage(slot.itemId, 1);
            else store.moveFromStorage(slot.itemId, 1);
            render();
          });
          c.add(hit);
        });
      };
      col(store.get().inventory.filter((s) => s.qty > 0), x + 26, true);
      col(store.get().storage.filter((s) => s.qty > 0), x + w / 2 + 16, false);

      this.button(c, x + w / 2 - 50, y + h - 34, 100, 'Done', () => this.closeModal(), 0xb0a48f);
    };
    render();
  }

  // ---- crafting (workbench) ---------------------------------------------------

  openCrafting(): void {
    this.openRecipeMenu('Workbench', RECIPES, 'Craft');
  }

  openCooking(): void {
    this.openRecipeMenu('Stove', COOK_RECIPES, 'Cook');
  }

  private openRecipeMenu(title: string, recipes: Recipe[], verb: string): void {
    const render = (): void => {
      const c = this.openModal();
      const w = 300;
      const rowH = 48;
      const contentH = recipes.length * rowH + 8;
      const view = Math.min(contentH, this.scale.height - 160);
      const h = 50 + view + 38;
      const { x, y } = this.panel(c, w, h);
      this.label(c, x + w / 2, y + 16, title, 16, '#ffffff', 0.5);
      if (contentH > view) this.label(c, x + w - 14, y + 18, '⇅', 14, '#9fb39a', 1);
      const top = y + 38;
      this.scrollList(c, x, w, top, view, contentH, (content) => {
        recipes.forEach((r, i) => {
          const def = getItem(r.output);
          if (!def) return;
          const ry = top + 6 + i * rowH;
          const can = r.inputs.every((inp) => store.countItem(inp.itemId) >= inp.qty);
          const purpose =
            def.category === 'decoration'
              ? '🎨 For decorating'
              : def.energy && def.energy > 0
                ? `🍽 Food · +${def.energy} energy`
                : def.sellPrice > 0
                  ? `💰 Sells ${def.sellPrice}g each`
                  : '🔧 Useful tool';
          const purposeColor = def.category === 'decoration' ? '#e0bcec' : def.energy ? '#bce0a8' : def.sellPrice > 0 ? '#ffe9a8' : '#a8d8ea';
          content.add(this.add.image(x + 22, ry + 14, def.icon).setScale(1.4).setAlpha(can ? 1 : 0.5));
          this.label(content, x + 40, ry + 2, `${def.name} ×${r.outputQty}`, 12, '#ffffff');
          const costs = r.inputs.map((inp) => `${getItem(inp.itemId)?.name ?? inp.itemId} ×${inp.qty}`).join(', ');
          this.label(content, x + 40, ry + 17, costs, 9, can ? '#cfe0c4' : '#c79b8a');
          this.label(content, x + 40, ry + 31, purpose, 9, purposeColor);
          this.button(content, x + w - 76, ry + 8, 62, verb, () => this.craft(r, render), can ? 0x7dbf5a : 0x6a6258);
        });
      });
      this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Done', () => this.closeModal(), 0xb0a48f);
    };
    render();
  }

  private craft(r: Recipe, rerender: () => void): void {
    if (!r.inputs.every((inp) => store.countItem(inp.itemId) >= inp.qty)) {
      this.showToast('Need more materials', 'warn');
      return;
    }
    for (const inp of r.inputs) store.removeItem(inp.itemId, inp.qty);
    store.addItem(r.output, r.outputQty);
    playSfx(this, AUDIO.pop);
    this.showToast(`+${r.outputQty} ${getItem(r.output)?.name ?? r.output}`, 'good');
    rerender();
  }

  /** Eat an edible item to restore energy. */
  private useFertilizer(): void {
    const grown = this.world.fertilizeAll();
    if (grown > 0) {
      store.removeItem('fertilizer', 1);
      playSfx(this, AUDIO.plant);
      this.showToast(`🌱 ${grown} crop${grown > 1 ? 's' : ''} grew a little!`, 'good');
      this.hotbarSig = '';
      this.refresh();
    } else {
      this.showToast('No growing crops to fertilize', 'info');
    }
  }

  private eatItem(def: ItemDef): void {
    const c = this.openModal();
    const w = 240;
    const h = 132;
    const { x, y } = this.panel(c, w, h);
    c.add(this.add.image(x + w / 2, y + 38, def.icon).setScale(2));
    this.label(c, x + w / 2, y + 60, `Eat ${def.name}?  +${def.energy} energy`, 11, '#cfe0c4', 0.5);
    this.button(c, x + 20, y + h - 40, 90, 'Eat', () => {
      store.update((s) => {
        s.energy = Math.min(s.energyMax, s.energy + (def.energy ?? 0));
      });
      store.removeItem(def.id, 1);
      playSfx(this, AUDIO.pop);
      this.showToast(`Yum! +${def.energy} energy`, 'good');
      this.closeModal();
    }, 0x7dbf5a);
    this.button(c, x + w - 110, y + h - 40, 90, 'Cancel', () => this.closeModal(), 0xb0a48f);
  }

  // ---- goals ------------------------------------------------------------------

  openGoals(): void {
    const render = (): void => {
      const c = this.openModal();
      const w = 300;
      const h = 48 + GOALS.length * 40 + 34;
      const { x, y } = this.panel(c, w, h);
      this.label(c, x + w / 2, y + 16, 'Goals', 16, '#ffffff', 0.5);
      const s = store.get();
      GOALS.forEach((g, i) => {
        const ry = y + 42 + i * 40;
        const done = g.done(s);
        const claimed = s.goalsClaimed.includes(g.id);
        this.label(c, x + 16, ry + 2, g.name, 12, done ? '#c8f5b0' : '#ffffff');
        const detail = claimed ? 'Claimed ✓' : g.progress ? `${g.desc} · ${g.progress(s)}` : g.desc;
        this.label(c, x + 16, ry + 18, detail, 10, '#bcd0b4');
        if (done && !claimed) this.button(c, x + w - 74, ry, 60, 'Claim', () => this.claimGoal(g, render), 0x7dbf5a);
        else this.label(c, x + w - 18, ry + 9, claimed ? '✓' : '…', 14, claimed ? '#c8f5b0' : '#8aa085', 1);
      });
      this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Done', () => this.closeModal(), 0xb0a48f);
    };
    render();
  }

  private claimGoal(g: Goal, rerender: () => void): void {
    const s = store.get();
    if (s.goalsClaimed.includes(g.id) || !g.done(s)) return;
    store.update((st) => {
      st.goalsClaimed.push(g.id);
      if (g.reward.gold) st.money += g.reward.gold;
    });
    if (g.reward.item) store.addItem(g.reward.item, g.reward.qty ?? 1);
    playSfx(this, AUDIO.coin);
    const rw = g.reward.gold ? `+${g.reward.gold}g` : `+${g.reward.qty ?? 1} ${getItem(g.reward.item ?? '')?.name ?? 'item'}`;
    this.showToast(`${g.name}!  ${rw}`, 'good');
    rerender();
  }

  private buy(itemId: string, price: number, name: string, rerender: () => void): void {
    const def0 = getItem(itemId);
    if (def0?.category === 'animal' && store.get().animals.length >= barnCapacity(store.get().barnLevel)) {
      this.showToast('🐄 The barn is full — upgrade it for more room!', 'warn');
      return;
    }
    const r = purchase(store.get().money, price);
    if (!r.ok) {
      this.showToast('Not enough gold', 'warn');
      return;
    }
    store.update((s) => (s.money = r.money));
    const def = getItem(itemId);
    if (def && def.category === 'animal') this.world.buyAnimal(itemId);
    else store.addItem(itemId, 1);
    playSfx(this, AUDIO.coin);
    this.showToast(`Bought ${name}`, 'good');
    rerender();
  }

  // ---- stats / collection -----------------------------------------------------

  openStats(): void {
    const c = this.openModal();
    const w = 280;
    const s = store.get();
    const rows: [string, string][] = [
      ['Farm', personalization.farmName],
      ['Date', `${seasonOf(s.clock.day).name} · Day ${s.clock.day}`],
      ['Year', `${yearOf(s.clock.day)}`],
      ['Gold', `${s.money}g`],
      ['Total earned', `${s.stats.totalEarned}g`],
      ['Crops harvested', `${s.stats.cropsHarvested}`],
      ['Animals raised', `${s.animals.length}`],
      ['Decorations placed', `${s.decorations.length}`],
      ['Goals completed', `${s.goalsClaimed.length}/${GOALS.length}`],
      [`${personalization.clara.name} hearts`, `${s.claraHearts} 💛`],
      ['Pet', `${s.petName || (s.petType === 'dog' ? 'Dog' : 'Cat')}`],
      ['Days played', `${s.stats.daysPlayed}`],
    ];
    const h = 54 + rows.length * 24 + 54;
    const { x, y } = this.panel(c, w, h);
    this.label(c, x + w / 2, y + 16, 'Farm Stats', 16, '#ffffff', 0.5);
    rows.forEach(([k, v], i) => {
      const ry = y + 46 + i * 24;
      this.label(c, x + 18, ry, k, 12, '#bcd0b4');
      this.label(c, x + w - 18, ry, v, 12, '#ffffff', 1);
    });
    const bw = (w - 44) / 3;
    this.button(c, x + 16, y + h - 70, bw, 'Log', () => this.openCollection(), 0x8a7fb0);
    this.button(c, x + 22 + bw, y + h - 70, bw, 'Badges', () => this.openAchievements(), 0x8a7fb0);
    this.button(c, x + 28 + bw * 2, y + h - 70, bw, 'Diary', () => this.openJournal(), 0x8a7fb0);
    this.button(c, x + w / 2 - 50, y + h - 34, 100, 'Done', () => this.closeModal(), 0xb0a48f);
  }

  openCollection(): void {
    const items = Object.values(ITEMS).filter((i) => i.category === 'crop' || i.category === 'material');
    const collected = store.get().collected;
    const have = items.filter((i) => (collected[i.id] ?? 0) > 0).length;
    const cols = 5;
    const rows = Math.ceil(items.length / cols);
    const w = 300;
    const h = 64 + rows * 38 + 28;
    const c = this.openModal();
    const { x, y } = this.panel(c, w, h);
    this.label(c, x + w / 2, y + 14, 'Collection', 16, '#ffffff', 0.5);
    this.label(c, x + w / 2, y + 36, `${have} / ${items.length} found`, 12, '#cfe0c4', 0.5);
    const cw = (w - 20) / cols;
    items.forEach((it, i) => {
      const cx = x + 10 + (i % cols) * cw + cw / 2;
      const cy = y + 64 + Math.floor(i / cols) * 38;
      const n = collected[it.id] ?? 0;
      c.add(this.add.image(cx, cy, it.icon).setScale(1.5).setAlpha(n > 0 ? 1 : 0.2));
      if (n > 0) this.label(c, cx + 11, cy + 2, `${n}`, 10, '#f2c14e', 0.5);
      const hit = this.add.rectangle(cx, cy, cw, 36, 0xffffff, 0).setInteractive({ useHandCursor: true });
      hit.on('pointerup', () =>
        this.showToast(n > 0 ? `${it.name} — collected ${n}` : `${it.name} — not found yet`, n > 0 ? 'good' : 'info'),
      );
      c.add(hit);
    });
    const complete = have === items.length;
    if (complete && !store.get().flags['collection_done']) {
      this.button(c, x + 18, y + h - 32, (w - 48) / 2, '🏆 Claim 1000g', () => {
        store.update((s) => {
          s.flags['collection_done'] = true;
          s.money += 1000;
        });
        playSfx(this, AUDIO.coin);
        this.showToast('Collection complete! +1000g 🏆', 'good');
        this.openCollection();
      }, 0xf2c14e);
      this.button(c, x + w / 2 + 6, y + h - 32, (w - 48) / 2, 'Done', () => this.closeModal(), 0xb0a48f);
    } else {
      this.button(c, x + w / 2 - 50, y + h - 30, 100, 'Done', () => this.closeModal(), 0xb0a48f);
    }
  }

  private cycleHat(): void {
    const idx = HATS.findIndex((h) => h.id === getHat());
    setHat(HATS[(idx + 1) % HATS.length]!.id);
    this.world.applyHat();
    if (this.scene.isActive('Interior')) (this.scene.get('Interior') as InteriorScene).applyHat();
    if (this.scene.isActive('Forest')) (this.scene.get('Forest') as ForestScene).applyHat();
    if (this.scene.isActive('Beach')) (this.scene.get('Beach') as BeachScene).applyHat();
    if (this.scene.isActive('Mine')) (this.scene.get('Mine') as MineScene).applyHat();
  }

  // ---- settings ---------------------------------------------------------------

  showLetter(text: string): void {
    const c = this.openModal();
    const w = 280;
    const h = 264;
    const { x, y } = this.panel(c, w, h);
    c.add(this.add.rectangle(x + w / 2, y + 38, w - 48, h - 96, 0xfdf6e3).setOrigin(0.5, 0).setStrokeStyle(2, 0xd8c8a8));
    this.label(c, x + w / 2, y + 14, '💌 A letter for you', 14, '#ffe9a8', 0.5);
    c.add(
      this.add
        .text(x + w / 2, y + 62, text.replace(/\{name\}/g, personalization.playerDefaultName).replace(/\{clara\}/g, personalization.clara.name), {
          fontFamily: FONT,
          fontSize: '13px',
          color: '#3a2e1e',
          align: 'center',
          lineSpacing: 6,
        })
        .setOrigin(0.5, 0)
        .setResolution(2),
    );
    this.button(c, x + w / 2 - 50, y + h - 36, 100, 'Aww 💛', () => this.closeModal(), 0xb0a48f);
  }

  openAchievements(): void {
    const c = this.openModal();
    const w = 300;
    const rowH = 42;
    const contentH = ACHIEVEMENTS.length * rowH + 8;
    const view = Math.min(contentH, this.scale.height - 150);
    const h = 50 + view + 38;
    const { x, y } = this.panel(c, w, h);
    const s = store.get();
    this.label(c, x + w / 2, y + 16, `🏅 Badges  ${achievementsDone(s)}/${ACHIEVEMENTS.length}`, 14, '#ffffff', 0.5);
    const top = y + 38;
    this.scrollList(c, x, w, top, view, contentH, (content) => {
      ACHIEVEMENTS.forEach((a, i) => {
        const ry = top + 6 + i * rowH;
        const done = a.done(s);
        content.add(this.add.image(x + 24, ry + 14, a.icon).setScale(1.5).setAlpha(done ? 1 : 0.3));
        this.label(content, x + 46, ry + 3, (done ? '✓ ' : '') + a.name, 12, done ? '#9ff09f' : '#cfcfcf');
        this.label(content, x + 46, ry + 20, a.desc, 10, '#b8c4b0');
      });
    });
    this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Done', () => this.closeModal(), 0xb0a48f);
  }

  openJournal(): void {
    const c = this.openModal();
    const w = 300;
    const entries = store.get().journal;
    const rowH = 30;
    const contentH = Math.max(rowH, entries.length * rowH) + 8;
    const view = Math.min(contentH, this.scale.height - 150);
    const h = 50 + view + 38;
    const { x, y } = this.panel(c, w, h);
    this.label(c, x + w / 2, y + 16, '📔 My Diary', 15, '#ffffff', 0.5);
    const top = y + 38;
    this.scrollList(c, x, w, top, view, contentH, (content) => {
      if (entries.length === 0) this.label(content, x + w / 2, top + 18, 'Your story starts today…', 12, '#b8c4b0', 0.5);
      entries.forEach((e, i) => {
        const ry = top + 8 + i * rowH;
        this.label(content, x + 16, ry, `Day ${e.day}`, 10, '#f2c14e');
        this.label(content, x + 72, ry, e.text, 12, '#ffffff');
      });
    });
    this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Done', () => this.closeModal(), 0xb0a48f);
  }

  /** An old-school "game manual" — a paginated storybook guide for Abigayle. */
  openManual(): void {
    const pages: { title: string; icon: string; lines: string[] }[] = [
      { title: 'Welcome!', icon: TX.heart, lines: ['Welcome to your farm!', 'Tap anywhere to walk around.', 'Explore, plant, and play. 💛'] },
      { title: 'Getting Around', icon: TX.player, lines: ['Tap a spot to walk there,', 'or hold & drag to wander.', 'Tap people, doors, and animals', 'to say hi or go inside.'] },
      { title: 'Tools', icon: TX.toolHoe, lines: ['Tap a tool (bottom), then tap.', 'Auto = does the right thing.', 'Hoe digs · Can waters · Axe chops', 'Scythe cuts · Rod fishes.'] },
      { title: 'Growing Crops', icon: TX.toolWater, lines: ['Buy seeds at Pip\'s Stall.', 'Dig soil, plant, then water.', 'Wait a few days, then tap', 'the grown plant to pick it!'] },
      { title: 'Making Money', icon: TX.coin, lines: ['Tap Pip\'s Stall, then Sell,', 'to sell crops, fish & goods.', 'Pip also Buys seeds,', 'animals, and décor.'] },
      { title: 'Your House', icon: TX.house, lines: ['Tap the house to go inside:', 'sleep, cook, craft & store —', 'and place furniture & décor.', 'Renovate it all the way', 'up to a castle! 🏰'] },
      { title: 'Animals', icon: TX.cow, lines: ['Buy ducks, chickens, sheep,', 'pigs & cows at Pip\'s.', 'Tap them each morning for', 'eggs, milk, wool & truffles.', 'Tap the barn for more room!'] },
      { title: `${personalization.clara.name} & Your Pet`, icon: TX.clara, lines: [`Give ${personalization.clara.name} treats for hearts —`, 'she\'ll ask for snacks she loves!', 'Tap her for a happy hug, and', 'pet your cat or dog for energy.'] },
      { title: 'Out & About', icon: TX.treefort, lines: ['Follow the wooden signs to a', '🌲 forest · 🏖 beach · ⛏ mine.', 'Tap the farm sign to read the', 'almanac (weather & crop tips).'] },
      { title: 'The Town', icon: TX.npcA, lines: ['From the forest, visit town!', 'Meet Rosie, Sam & Old Joe,', 'shop, wish at the well, play', 'Whack-a-Mole, and check the', 'notice board for a daily job.'] },
      { title: 'Energy & Sleep', icon: TX.energyPip, lines: ['Chores use energy (green bar).', 'Eat food or sleep to refill.', 'Tap the bed to sleep and', 'start a brand-new day.'] },
      { title: 'Seasons & Weather', icon: TX.leaf, lines: ['Seasons change all year —', 'plant the right crops each one.', 'Rainy days water your crops,', 'and festivals bring confetti!'] },
      { title: 'Have Fun!', icon: TX.shootingstar, lines: ['There\'s no wrong way to play.', 'Plant, explore, and decorate.', 'Watch for shooting stars', 'at night. Made with love. 💛'] },
    ];
    let page = 0;
    const render = (): void => {
      const c = this.openModal();
      const w = 300;
      const h = 326;
      const { x, y } = this.panel(c, w, h);
      const pg = pages[page]!;
      this.label(c, x + w / 2, y + 16, `📖  ${pg.title}`, 15, '#ffe9a8', 0.5);
      const img = this.add.image(x + w / 2, y + 66, pg.icon);
      img.setScale(Math.min(46 / img.width, 46 / img.height));
      c.add(img);
      pg.lines.forEach((ln, i) => this.label(c, x + w / 2, y + 104 + i * 24, ln, 12, '#ffffff', 0.5));
      this.button(c, x + 16, y + h - 34, 60, '‹', () => { if (page > 0) { page--; render(); } }, 0x6f87b0);
      this.label(c, x + w / 2, y + h - 26, `${page + 1} / ${pages.length}`, 11, '#cfe0c4', 0.5);
      this.button(c, x + w - 76, y + h - 34, 60, page < pages.length - 1 ? '›' : 'Done', () => {
        if (page < pages.length - 1) {
          page++;
          render();
        } else {
          this.closeModal();
        }
      }, page < pages.length - 1 ? 0x6f87b0 : 0xb0a48f);
    };
    render();
  }

  /** A heartfelt credits screen — celebrating the player as a co-creator of her own gift. 💛 */
  openCredits(): void {
    const name = personalization.playerDefaultName;
    confetti(this);
    const c = this.openModal();
    const w = 304;
    const h = 232 + PLAYER_IDEAS.length * 36;
    const { x, y } = this.panel(c, w, h);
    this.label(c, x + w / 2, y + 18, '💛 Sandy Hollow', 17, '#ffe9a8', 0.5);
    this.label(c, x + w / 2, y + 44, 'A farm made with love, just for you.', 11, '#ffffff', 0.5);
    this.label(c, x + w / 2, y + 74, `Designed, in part, by ${name} —`, 12, '#cfe0c4', 0.5);
    this.label(c, x + w / 2, y + 92, 'who typed her own ideas and became', 10, '#cfe0c4', 0.5);
    this.label(c, x + w / 2, y + 107, 'a game developer. 🌟', 11, '#cfe0c4', 0.5);
    PLAYER_IDEAS.forEach((it, i) => {
      const yy = y + 134 + i * 36;
      this.label(c, x + 22, yy, `✨ ${it.feature}`, 12, '#ffe9a8');
      this.label(c, x + 30, yy + 16, it.note, 9, '#bcd0b4');
    });
    this.label(c, x + w / 2, y + h - 62, 'Every farm has a maker.', 11, '#ffffff', 0.5);
    this.label(c, x + w / 2, y + h - 46, `This one has two. 💛`, 12, '#ffe9a8', 0.5);
    this.button(c, x + w / 2 - 50, y + h - 30, 100, 'Done', () => this.closeModal(), 0xb0a48f);
  }

  openSettings(): void {
    const render = (): void => {
      const c = this.openModal();
      const w = 250;
      const h = 412;
      const { x, y } = this.panel(c, w, h);
      this.label(c, x + w / 2, y + 18, 'Settings', 18, '#ffffff', 0.5);
      this.label(c, x + w - 12, y + 12, APP_VERSION, 10, '#7a8a78', 1);
      this.toggleRow(c, x + 20, y + 50, w - 40, 'Music', isMusicEnabled(), (b) => {
        setMusicEnabled(b);
        render();
      });
      this.toggleRow(c, x + 20, y + 88, w - 40, 'Sound', isSfxEnabled(), (b) => {
        setSfxEnabled(b);
        render();
      });
      this.button(c, x + 20, y + 126, w - 40, '✨ Customize', () => this.openCustomize(), 0x8a7fb0);
      this.button(c, x + 20, y + 162, w - 40, '📖 How to Play', () => this.openManual(), 0x7d9f6a);
      this.button(c, x + 20, y + 198, w - 40, '☁ Cloud / farm code', () => this.openCloud(), 0x6fa8c8);
      this.button(c, x + 20, y + 234, w - 40, 'Switch farm', () => {
        saveGame();
        location.reload();
      }, 0x6f87b0);
      this.button(c, x + 20, y + 306, w - 40, '💛 Credits', () => this.openCredits(), 0xc88a9a);
      this.button(c, x + 20, y + 270, w - 40, 'Start a new farm', () => this.confirmNewFarm(), 0xc56a5a);
      this.button(c, x + w / 2 - 50, y + h - 34, 100, 'Done', () => this.closeModal(), 0xb0a48f);
    };
    render();
  }

  openCustomize(): void {
    const render = (): void => {
      const c = this.openModal();
      const w = 270;
      const h = 404;
      const { x, y } = this.panel(c, w, h);
      this.label(c, x + w / 2, y + 18, 'Customize', 18, '#ffffff', 0.5);
      this.button(c, x + 20, y + 50, w - 40, `Your hat: ${HATS.find((hh) => hh.id === getHat())?.name ?? 'None'}`, () => {
        this.cycleHat();
        render();
      }, 0x8a7fb0);
      this.button(c, x + 20, y + 88, w - 40, `Pet: ${store.get().petType === 'dog' ? 'Dog 🐶' : 'Cat 🐱'}`, () => {
        store.update((s) => (s.petType = s.petType === 'dog' ? 'cat' : 'dog'));
        this.world.applyPetTexture();
        render();
      }, 0x8a7fb0);
      this.button(c, x + 20, y + 126, w - 40, `Pet name: ${store.get().petName || '(tap to name)'}`, () => {
        const name = window.prompt('Name your pet', store.get().petName || '');
        if (name != null) store.update((s) => (s.petName = name.slice(0, 16)));
        render();
      }, 0x6f87b0);
      const co = CLARA_OUTFITS.find((o) => o.id === getClaraOutfit());
      const next = CLARA_OUTFITS.filter((o) => store.get().claraHearts < o.unlock)[0];
      this.button(c, x + 20, y + 164, w - 40, `${personalization.clara.name}: ${co?.name ?? 'None'}${next ? ` (next ${next.unlock}💛)` : ''}`, () => {
        this.cycleClaraOutfit();
        render();
      }, 0x8a7fb0);
      this.button(c, x + 20, y + 202, w - 40, `Pet collar: ${COLLARS.find((cc) => cc.id === getCollar())?.name ?? 'None'}`, () => {
        const i = COLLARS.findIndex((cc) => cc.id === getCollar());
        setCollar(COLLARS[(i + 1) % COLLARS.length]!.id);
        this.world.applyPetCollar();
        render();
      }, 0x8a7fb0);
      this.button(c, x + 20, y + 240, w - 40, `Big text: ${this.bigText ? 'On ✓' : 'Off'}`, () => {
        this.bigText = !this.bigText;
        try {
          localStorage.setItem('sandyhollow.bigtext', this.bigText ? '1' : '0');
        } catch {
          /* ignore */
        }
        render();
      }, 0x8a7fb0);
      this.button(c, x + 20, y + 278, w - 40, `Toddler mode: ${store.toddlerMode ? 'On ✓' : 'Off'}`, () => {
        store.setToddlerMode(!store.toddlerMode);
        this.refresh();
        render();
      }, 0x7d9f6a);
      this.button(c, x + 20, y + 316, w - 40, '✏️ Edit your details / photo', () => {
        void showProfileForm().then(() => location.reload());
      }, 0x6f87b0);
      this.button(c, x + w / 2 - 50, y + h - 34, 100, 'Back', () => this.openSettings(), 0xb0a48f);
    };
    render();
  }

  private cycleClaraOutfit(): void {
    const hearts = store.get().claraHearts;
    const unlocked = CLARA_OUTFITS.filter((o) => hearts >= o.unlock);
    const idx = unlocked.findIndex((o) => o.id === getClaraOutfit());
    setClaraOutfit(unlocked[(idx + 1) % unlocked.length]!.id);
    this.world.applyClaraOutfit();
  }

  private toggleRow(
    c: Phaser.GameObjects.Container,
    x: number,
    y: number,
    w: number,
    text: string,
    on: boolean,
    onToggle: (b: boolean) => void,
  ): void {
    this.label(c, x, y + 6, text, 14, '#ffffff');
    const bw = 64;
    const bx = x + w - bw;
    const g = this.add.graphics();
    g.fillStyle(on ? 0x6fcf57 : 0x70665a, 1);
    g.fillRoundedRect(bx, y, bw, 26, 6);
    c.add(g);
    c.add(
      this.add
        .text(bx + bw / 2, y + 13, on ? 'On' : 'Off', { fontFamily: FONT, fontSize: '13px', color: '#1c241d' })
        .setOrigin(0.5)
        .setResolution(2),
    );
    const hit = this.add.rectangle(bx, y, bw, 26, 0xffffff, 0).setOrigin(0, 0).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => onToggle(!on));
    c.add(hit);
  }

  private openCloud(): void {
    const c = this.openModal();
    const w = 290;
    const h = 252;
    const { x, y } = this.panel(c, w, h);
    this.label(c, x + w / 2, y + 16, '☁ Cloud Save', 16, '#ffffff', 0.5);
    this.label(c, x + 16, y + 44, 'Your farm syncs automatically when', 11, '#bcd0b4');
    this.label(c, x + 16, y + 58, 'online. To move to a new phone, copy', 11, '#bcd0b4');
    this.label(c, x + 16, y + 72, 'this code and paste it on the other:', 11, '#bcd0b4');
    const codeLabel = this.add
      .text(x + 16, y + 94, '…', { fontFamily: FONT, fontSize: '10px', color: '#ffe9a8', wordWrap: { width: w - 32 } })
      .setResolution(2);
    c.add(codeLabel);
    void getFarmCode().then((code) => codeLabel.setText(code));
    this.button(c, x + 16, y + 150, w - 32, 'Copy code', () => {
      void getFarmCode().then((code) => {
        try {
          void navigator.clipboard.writeText(code);
        } catch {
          /* ignore */
        }
        this.showToast('Code copied', 'good');
      });
    }, 0x7dbf5a);
    this.button(c, x + 16, y + 188, w - 32, 'Restore from a code', () => {
      const code = window.prompt('Paste your farm code from the other phone:');
      if (code && setFarmCode(code)) {
        this.showToast('Restoring…', 'good');
        void pullIfNewer().then(() => location.reload());
      } else if (code) {
        this.showToast('That code looks wrong', 'warn');
      }
    }, 0x6fa8c8);
    this.button(c, x + w / 2 - 50, y + h - 32, 100, 'Done', () => this.closeModal(), 0xb0a48f);
  }

  private confirmNewFarm(): void {
    const c = this.openModal();
    const w = 240;
    const h = 130;
    const { x, y } = this.panel(c, w, h);
    this.label(c, x + w / 2, y + 26, 'Start a new farm?', 15, '#ffffff', 0.5);
    this.label(c, x + w / 2, y + 50, 'This erases the current one.', 10, '#ffcaa8', 0.5);
    this.button(c, x + 16, y + 80, 92, 'Erase', () => {
      clearSave();
      location.reload();
    }, 0xc56a5a);
    this.button(c, x + w - 108, y + 80, 92, 'Cancel', () => this.closeModal(), 0xb0a48f);
  }
}
